// Async data + defensive readers for the non-card tenders (gift card / loyalty / store credit).
// Loyalty config is synced locally (IDB meta "loyalty_program"); gift-card balances come from the
// register lookup API; customer balances are read defensively from the attached customer object.

import { getMeta } from "./db";
import { phpGiftCards } from "./api";
import { toNum } from "../engine/money";
import { loyaltyBonusPoints } from "../engine/loyaltyPromo";

function safeJson(s) {
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}

function pick(o, ...keys) {
  if (!o) return "";
  for (const k of keys) if (o[k] != null && o[k] !== "") return o[k];
  return "";
}


/**
 * Loyalty program config (synced to IDB meta "loyalty_program" from the Go bundle).
 * Go GetLoyaltyProgramResponse fields: enable_loyalty, points_per_dollar,
 * redemption_value (= dollars per point on redeem), min_points_redemption.
 * @returns {Promise<null|{enabled:boolean, dollarPerPoint:number, pointsPerDollar:number, minPointsRedemption:number, raw:object}>}
 */
export async function getLoyaltyConfig() {
  const raw = await getMeta("loyalty_program");
  if (!raw) return null;
  const c = typeof raw === "string" ? safeJson(raw) : raw;
  if (!c) return null;
  const promos = c.promotion_array ?? c.promotionArray;
  return {
    enabled: String(c.enable_loyalty ?? c.enableLoyalty ?? "") === "1",
    dollarPerPoint: toNum(c.redemption_value ?? c.redemptionValue),
    pointsPerDollar: toNum(c.points_per_dollar ?? c.pointsPerDollar),
    // Σ bonus points from promotions active right now (Android getTotalLoyaltyPoints); earn rate = base + bonus.
    bonusPointsPerDollar: loyaltyBonusPoints(promos),
    minPointsRedemption: toNum(c.min_points_redemption ?? c.minPointsRedemption),
    raw: c,
  };
}

/** Customer's store-credit balance (defensive field names). */
export function customerStoreCredit(customer) {
  return toNum(pick(customer, "total_store_credit", "store_credit", "storecredit", "sc_balance"));
}

/**
 * Customer's available loyalty balance in DOLLARS + points (defensive). Some APIs expose bonus
 * dollars directly; others expose points (converted via dollarPerPoint). `known` is false when the
 * balance can't be determined — loyalty redemption is disabled in that case (avoids over-redeeming
 * a balance we can't see, which the server would debit into the negative).
 */
export function customerLoyaltyBalance(customer, dollarPerPoint) {
  if (!customer) return { dollars: 0, points: 0, known: false };
  const dollarsDirect = pick(customer, "total_bonus_dollar", "bonus_dollars", "loyalty_dollars", "total_loyalty_amount");
  // ⚠️ App/find_customer returns the point balance as `total_loyalty_pts` (verified live 2026-08-27:
  // Abhi = "398.89"). It was absent from this list, so loyalty redemption saw 0 points and stayed
  // disabled for customers who actually had a balance. `total_bonus_points` is the Android model key.
  const pointsRaw = pick(customer, "total_loyalty_pts", "total_bonus_points", "total_loyalty_points", "loyalty_points", "current_points", "total_points", "points");
  if (dollarsDirect !== "") return { dollars: toNum(dollarsDirect), points: toNum(pointsRaw), known: true };
  if (pointsRaw !== "") {
    const dpp = toNum(dollarPerPoint);
    return { dollars: dpp > 0 ? toNum(pointsRaw) * dpp : 0, points: toNum(pointsRaw), known: dpp > 0 };
  }
  return { dollars: 0, points: 0, known: false };
}

/**
 * Look up a gift card by number → current balance. Mirrors the Android register flow
 * (fetch all gift cards, find by number, read `amount` as the balance).
 * @returns {Promise<{found:boolean, number?:string, balance?:number, reason?:string}>}
 */
export async function lookupGiftCard(number) {
  const num = String(number || "").trim();
  if (!num) return { found: false, reason: "Enter a gift card number" };
  let res;
  try {
    res = await phpGiftCards();
  } catch {
    return { found: false, reason: "Gift card lookup failed" };
  }
  const rows = res && Array.isArray(res.result) ? res.result : [];
  const row = rows.find((g) => String(g.number).trim() === num);
  if (!row) return { found: false, reason: "Gift card not found" };
  const balance = toNum(row.amount);
  if (balance <= 0) return { found: false, reason: "Gift card balance is $0.00" };
  return { found: true, number: num, balance };
}
